// Onglet « Numérotation ».
//
// La référence d'un devis est attribuée par le SERVEUR, au format
// `AAAA-MM-NNN`, avec un compteur mensuel. C'était la correction la plus
// visible d'AGC : là-bas la référence était saisie à la main et
// l'unicité laissée à une contrainte SQL, donc deux devis créés à la
// même minute produisaient une violation de contrainte au visage de
// l'utilisateur.
//
// Cet écran ne sert qu'à UNE chose : caler le compteur le jour d'une
// reprise d'historique. « On a déjà émis 14 devis ce mois-ci dans
// l'ancien système, le prochain doit être le 15. »
//
// Le piège qu'il doit rendre impossible : `next` n'est pris en compte
// par `nextReference` que si l'année ET le mois enregistrés sont ceux du
// mois courant. Poser `next` seul, sur un état daté d'un autre mois, ne
// produirait strictement rien — en silence. Le panneau écrit donc
// toujours les trois valeurs ensemble, et affiche la référence qui sera
// réellement attribuée.

import { useCallback, useState, useEffect } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import NumberField from '../../ui/NumberField.jsx';
import Button from '../../ui/Button.jsx';
import Badge from '../../ui/Badge.jsx';
import { ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';

/** La règle de `reference.service.js`, à l'identique : le compteur ne
 *  s'applique qu'au mois qu'il porte. Un autre mois repart à 1. */
function nextReferenceOf(numbering, now) {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const sameMonth = Number(numbering.year) === year && Number(numbering.month) === month;
  const seq = sameMonth && Number(numbering.next) > 0 ? Number(numbering.next) : 1;
  return `${year}-${String(month).padStart(2, '0')}-${String(seq).padStart(3, '0')}`;
}

export function NumerotationPanel({ isAdmin }) {
  const toast = useToast();
  const load = useCallback(() => api.get('/parametres'), []);
  const { data, error, loading, reload } = useResource(load);
  const [numbering, setNumbering] = useState(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (data) setNumbering(data.settings.numbering ?? null);
  }, [data]);

  if (loading || !numbering) return <SkeletonRows rows={4} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  // L'état enregistré porte-t-il bien le mois courant ? Si non, le
  // compteur affiché ne s'applique PAS et le prochain devis repartira
  // à 001. Il faut que ça se voie.
  const staleMonth = Number(numbering.year) !== year || Number(numbering.month) !== month;

  async function save(value) {
    setPending(true);
    try {
      await api.put('/parametres/numbering', value);
      toast.success('Numérotation enregistrée.');
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPending(false);
    }
  }

  /** Caler le compteur revient TOUJOURS à écrire les trois valeurs pour
   *  le mois courant : poser `next` seul serait sans effet. */
  const calibrate = () =>
    save({ year, month, next: Math.max(1, Number(numbering.next) || 1) });

  return (
    <>
      <Card>
        <CardHead title="Prochaine référence">
          {staleMonth ? <Badge tone="warn">Compteur d’un autre mois</Badge> : null}
        </CardHead>
        <CardBody className="stack">
          <p className="devis-total num">{nextReferenceOf(numbering, now)}</p>
          <p className="field__hint">
            C’est la référence que recevra le prochain devis créé. Elle est attribuée par le
            serveur, jamais saisie : deux devis créés en même temps ne peuvent pas entrer en
            collision.
          </p>
          {staleMonth ? (
            <p className="field__hint">
              Le compteur enregistré porte{' '}
              {numbering.year && numbering.month
                ? `${String(numbering.month).padStart(2, '0')}/${numbering.year}`
                : 'aucun mois'}
              , et non le mois courant : il ne s’applique donc pas, et la numérotation repartira à
              001. C’est le comportement normal au changement de mois.
            </p>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Caler le compteur">
          {isAdmin ? (
            <Button size="sm" variant="primary" disabled={pending} onClick={calibrate}>
              Enregistrer
            </Button>
          ) : null}
        </CardHead>
        <CardBody className="stack">
          <p className="field__hint">
            Utile le jour d’une reprise d’historique : si {String(month).padStart(2, '0')}/{year}{' '}
            compte déjà 14 devis émis dans l’ancien système, posez 15 ici. L’enregistrement cale le
            compteur sur le <strong>mois courant</strong> — poser un numéro sans son mois n’aurait
            aucun effet.
          </p>
          <div className="grid-2">
            <NumberField
              label="Prochain numéro"
              value={numbering.next}
              min={1}
              disabled={!isAdmin}
              hint={`Donnera ${year}-${String(month).padStart(2, '0')}-${String(
                Math.max(1, Number(numbering.next) || 1),
              ).padStart(3, '0')}.`}
              onChange={(v) => setNumbering({ ...numbering, next: v })}
            />
          </div>
          <p className="field__hint">
            Si le numéro posé est déjà pris, le serveur passe au suivant libre au lieu d’échouer :
            un compteur mal calé se rattrape tout seul, il ne bloque pas la création d’un devis.
            Une révision, elle, ne consomme aucun numéro — elle reprend la référence de son parent
            suffixée d’une lettre, pour que le client reconnaisse son devis.
          </p>
        </CardBody>
      </Card>
    </>
  );
}

export default NumerotationPanel;
